import { CreateElement } from "vue";
import i18n from "@/i18n";
import _ from "@/boot/lodash";
import { DataModules } from "@/store/modules/data";
import { CatalogueProductFilters as ProductsFilters } from "@/data/filters/products";
import { productsSorters as ProductsSorters } from "@/data/sorters/products";
import { IImportMappingSource } from "@/models/imports";
import { IProduct } from "@/models/products";
import ProductImage from "@/components/app/global/products/productImage.vue";
import { PurchaseTypes } from "@/data/enums/products";
import { ProductTypes } from "@/data/enums/products";

const availableTerms = (product: IProduct) => {
  return i18n.tc(
    "_plurals.available_terms",
    _.uniqBy(product.prices, "billing_cycle_months").length
  );
};

const paymentValue = (product: IProduct) => {
  return product.billing_cycle_months === PurchaseTypes.RECURRING
    ? `${i18n.t("_.recurring")} (${availableTerms(product)})`
    : `${i18n.t("_.one_off")}`;
};

const productRoute = (product: IProduct) => {
  return {
    name: "adminCatalogueProduct",
    params: {
      productId: product.id
    }
  };
};

const firstLabel = (
  h: CreateElement,
  product: IProduct,
  onClick: Function = () => ({})
) => {
  return h("p", {}, [
    h("span", { class: "has-text-grey" }, `${product.category.name} / `),
    h(
      "u-link",
      { props: { to: productRoute(product) }, on: { click: onClick } },
      [h("strong", {}, product.name)]
    )
  ]);
};

const secondLabel = (h: CreateElement, product: IProduct) => {
  return h("p", { class: "is-size-8" }, [
    h(
      "strong",
      {
        class: `is-uppercased ${
          product.available_for_sales ? "has-text-success" : "has-text-grey"
        }`
      },
      `${
        product.available_for_sales ? i18n.t("_.active") : i18n.t("_.inactive")
      }`
    ),
    h("strong", { class: "has-text-grey" }, ` - ${paymentValue(product)}`)
  ]);
};

const media = (h: CreateElement, product: IProduct) => {
  const mediaSrc = product.image?.full_url;
  return mediaSrc
    ? h("u-square-image", { props: { src: mediaSrc } }, [])
    : h(ProductImage, { props: { product: {} } }, []);
};

export const ProductSource: IImportMappingSource = {
  props: {
    availableFilters: ProductsFilters,
    availableSorters: ProductsSorters,
    usedDataModule: DataModules.CATALOGUE,
    guardFunctionalities: ["list_products"],
    storeParams: querySearch => {
      return {
        with: ["category", "image", "prices"].join(),
        "filter[product_type]": [
          ProductTypes.SINGLE_PRODUCT,
          ProductTypes.PRODUCT_BUNDLE
        ].join(","),
        ...(querySearch ? { query: querySearch } : {})
      };
    }
  },
  render: {
    id: (product: IProduct) => product.id,
    firstLabel: (h: CreateElement, product: IProduct, onClick: Function) =>
      firstLabel(h, product, onClick),
    secondLabel: (h: CreateElement, product: IProduct) =>
      secondLabel(h, product),
    media: (h: CreateElement, product: IProduct) => media(h, product)
  }
};
